import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

/** Per-doctor overrides — blank / inherit = clinic booking settings. */
export default function DoctorBookingOverrides({ booking, onBooking }) {
  const set = (key, value) => onBooking({ ...booking, [key]: value });
  const mode = booking.booking_mode ?? '';
  // slots → slot length; daily_count → quota only; inherit → show both so either can be set
  const showSlotMinutes = mode === 'slots' || mode === '';
  const showMaxDay = mode === 'daily_count' || mode === 'slots' || mode === '';

  const inheritNumber = (key, label) => (
    <div className="space-y-1">
      <Label className="text-[11px] text-zinc-500">{label}</Label>
      <Input
        type="number"
        placeholder="Clinic default"
        value={booking[key] ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          set(key, raw === '' ? null : Number(raw));
        }}
        data-testid={`doctor-booking-${key}`}
      />
    </div>
  );

  return (
    <div className="mt-3 rounded-lg border bg-white px-3 py-3 space-y-3" data-testid="doctor-booking-overrides">
      <p className="text-xs font-semibold text-zinc-700">Overrides (optional)</p>
      <div className="space-y-1">
        <Label className="text-[11px] text-zinc-500">Booking style</Label>
        <select
          className="w-full h-10 border rounded-md px-2 text-sm bg-white"
          value={mode}
          onChange={(e) => {
            const next = e.target.value === '' ? null : e.target.value;
            const patch = { ...booking, booking_mode: next };
            if (next === 'daily_count') patch.slot_minutes = null;
            onBooking(patch);
          }}
          data-testid="doctor-booking-booking_mode"
        >
          <option value="">Use clinic default</option>
          <option value="slots">Time slots</option>
          <option value="daily_count">Appointments per day</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {showSlotMinutes ? inheritNumber('slot_minutes', 'Minutes per slot') : null}
        {showMaxDay ? inheritNumber('max_appointments_per_day', 'Max / day') : null}
        {inheritNumber('max_walkins_per_day', 'Max walk-ins / day (0 = unlimited)')}
        {inheritNumber('patient_book_hours_prior', 'Book hours prior')}
        {inheritNumber('patient_cancel_hours_prior', 'Cancel hours prior')}
      </div>
      <p className="text-[11px] text-zinc-400">
        Walk-in cap applies to reception desk registrations. Leave blank to use clinic default.
        Set 0 for unlimited walk-ins for this doctor.
      </p>
      {mode === 'daily_count' ? (
        <p className="text-[11px] text-zinc-400">
          Daily quota mode — patients request a place for the day (no time grid).
        </p>
      ) : null}
      {mode === 'slots' ? (
        <p className="text-[11px] text-zinc-400">
          Time-slot mode — patients pick a clock time using slot length below.
        </p>
      ) : null}
    </div>
  );
}
